import type { ReactElement } from 'react';
import { Box, Text, useMantineTheme } from '@mantine/core';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { SurfaceCard } from '@/design/SurfaceCard';
import { makeCurrencyFormatter } from './CostFormatting';
import type { CostTrendPoint, CostTrendSeries } from './LicenseCostPageViewModel';

interface CostTrendChartProps {
    trend: CostTrendPoint[];
    series: CostTrendSeries[];
    currency: string;
}

export default function CostTrendChart({ trend, series, currency }: CostTrendChartProps): ReactElement {
    const theme = useMantineTheme();
    const seriesColors = [
        theme.colors.blue[6],
        theme.colors.yellow[6],
        theme.colors.teal[6],
        theme.colors.violet[6],
        theme.colors.orange[6],
        theme.colors.cyan[6],
        theme.colors.grape[6],
    ];
    const formatCurrency = makeCurrencyFormatter(currency);

    return (
        <SurfaceCard>
            <Text size="sm" fw={700} mb={4}>
                Spend trend
            </Text>
            <Text c="dimmed" mb="lg" style={{ fontSize: '11px' }}>
                Cost per provider · selected range.
            </Text>

            {trend.length === 0 ? (
                <Box py="xl" style={{ textAlign: 'center' }}>
                    <Text size="sm" c="dimmed">
                        No cost records in the selected range.
                    </Text>
                </Box>
            ) : (
                <Box style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--bm-color-border)" vertical={false} />
                            <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" />
                            <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" width={72} />
                            <Tooltip
                                formatter={(value: number) => formatCurrency(value)}
                                contentStyle={{
                                    background: 'var(--bm-color-body)',
                                    border: '1px solid var(--bm-color-border)',
                                    borderRadius: 'var(--bm-radius)',
                                    fontSize: 12,
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {series.map((s, index) => (
                                <Line
                                    key={s.id}
                                    type="monotone"
                                    dataKey={s.id}
                                    name={s.label}
                                    stroke={seriesColors[index % seriesColors.length]}
                                    strokeWidth={2}
                                    dot={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </SurfaceCard>
    );
}
